/**
 * Shared contract for per-user chat preferences (user_chat_preferences table).
 * Used by api/user/chat-preferences.ts (server) and src/lib/chatPreferencesStore.ts (client).
 *
 * Client shape is camelCase; DB row is snake_case. FIELD_DEFS is the single
 * source of truth for the mapping and per-field validation/clamping.
 */

export type StreamTimeoutPreset = "5" | "10" | "15" | "30" | "custom" | "none";
export type WebSearchMode = "open" | "closed";
export type ContextMode = "MAX" | "LITE";
export type ChatMode = "normal" | "questioning" | "dialogue";
export type VoiceMode = "off" | "record" | "speech";

// DS EDIT 8 — durable UI preferences ride the same synced store.
export type UiViewMode = "list" | "grid" | "table" | "card";
export interface SurfaceViewPreference {
  viewMode?: UiViewMode;
  sortField?: string;
  sortDirection?: "asc" | "desc";
}
export interface UiPreferences {
  /** Per-surface presentation (keyed by the surface's storageKey, e.g. "threads"). No entry = user never chose. */
  surfaces: Record<string, SurfaceViewPreference>;
  /** undefined = user never chose (UI default is visible). */
  showSuggestedPrompts?: boolean;
}

export interface UserChatPreferences {
  usePersonalization: boolean;
  useSourceOfTruth: boolean;
  preferredModel: string | null;
  autoSelectBest: boolean;
  preferredAgentId: string | null;
  selectedSuperPromptId: string | null;
  thinkingMode: boolean;
  webSearchMode: WebSearchMode;
  contextMode: ContextMode;
  chatMode: ChatMode;
  useRag: boolean;
  ragLimit: number;
  ragSimilarityThreshold: number;
  ragHybridMode: boolean;
  ragHybridSearch: boolean;
  ragUseRerank: boolean;
  autoEmbedDocuments: boolean;
  autoEmbedThreads: boolean;
  streamTimeoutPreset: StreamTimeoutPreset;
  streamCustomTimeoutMinutes: number;
  voiceMode: VoiceMode;
  ttsVoiceId: string | null;
  ttsSpeed: number;
  uiPreferences: UiPreferences;
}

export const DEFAULT_CHAT_PREFERENCES: UserChatPreferences = {
  usePersonalization: false,
  useSourceOfTruth: true,
  preferredModel: null,
  autoSelectBest: false,
  preferredAgentId: null,
  selectedSuperPromptId: null,
  thinkingMode: false,
  webSearchMode: "open",
  contextMode: "MAX",
  chatMode: "normal",
  useRag: false,
  ragLimit: 8,
  ragSimilarityThreshold: 0.45,
  ragHybridMode: true,
  ragHybridSearch: true,
  ragUseRerank: false,
  autoEmbedDocuments: true,
  autoEmbedThreads: true,
  streamTimeoutPreset: "5",
  streamCustomTimeoutMinutes: 10,
  voiceMode: "off",
  ttsVoiceId: null,
  ttsSpeed: 1.0,
  uiPreferences: { surfaces: {} },
};

type Validator = (value: unknown) => unknown | undefined;

const bool: Validator = (v) => (typeof v === "boolean" ? v : undefined);
const nullableString: Validator = (v) =>
  v === null ? null : typeof v === "string" && v.length <= 500 ? v : undefined;
const oneOf =
  (allowed: readonly string[]): Validator =>
  (v) =>
    typeof v === "string" && allowed.includes(v) ? v : undefined;
const intInRange =
  (min: number, max: number): Validator =>
  (v) =>
    typeof v === "number" && Number.isFinite(v)
      ? Math.max(min, Math.min(max, Math.round(v)))
      : undefined;
const numInRange =
  (min: number, max: number): Validator =>
  (v) =>
    typeof v === "number" && Number.isFinite(v)
      ? Math.max(min, Math.min(max, v))
      : undefined;

const UI_SURFACE_KEY_MAX = 64;
const UI_SURFACE_MAX = 50;
const UI_VIEW_MODES: readonly string[] = ["list", "grid", "table", "card"];

/**
 * DS EDIT 8 — runtime sanitizer for the ui_preferences jsonb field. Unknown
 * fields are dropped, invalid entries are skipped, surface count is capped.
 * Returns undefined (→ safe default) for non-object input.
 */
export function sanitizeUiPreferences(value: unknown): UiPreferences | undefined {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const out: UiPreferences = { surfaces: {} };
  const rawSurfaces = raw.surfaces;
  if (typeof rawSurfaces === "object" && rawSurfaces !== null && !Array.isArray(rawSurfaces)) {
    for (const [key, entry] of Object.entries(rawSurfaces as Record<string, unknown>)) {
      if (Object.keys(out.surfaces).length >= UI_SURFACE_MAX) break;
      if (!key || key.length > UI_SURFACE_KEY_MAX) continue;
      if (typeof entry !== "object" || entry === null || Array.isArray(entry)) continue;
      const e = entry as Record<string, unknown>;
      const clean: SurfaceViewPreference = {};
      if (typeof e.viewMode === "string" && UI_VIEW_MODES.includes(e.viewMode)) {
        clean.viewMode = e.viewMode as UiViewMode;
      }
      if (typeof e.sortField === "string" && e.sortField.length > 0 && e.sortField.length <= UI_SURFACE_KEY_MAX) {
        clean.sortField = e.sortField;
      }
      if (e.sortDirection === "asc" || e.sortDirection === "desc") {
        clean.sortDirection = e.sortDirection;
      }
      if (Object.keys(clean).length > 0) out.surfaces[key] = clean;
    }
  }
  if (typeof raw.showSuggestedPrompts === "boolean") {
    out.showSuggestedPrompts = raw.showSuggestedPrompts;
  }
  return out;
}

/** Partial-update-safe merge for one surface's presentation preference. */
export function mergeSurfacePreference(
  current: UiPreferences,
  surfaceKey: string,
  partial: SurfaceViewPreference
): UiPreferences {
  const existing = current.surfaces[surfaceKey] ?? {};
  return {
    ...current,
    surfaces: { ...current.surfaces, [surfaceKey]: { ...existing, ...partial } },
  };
}

export const FIELD_DEFS: Record<keyof UserChatPreferences, { column: string; validate: Validator }> = {
  usePersonalization: { column: "use_personalization", validate: bool },
  useSourceOfTruth: { column: "use_source_of_truth", validate: bool },
  preferredModel: { column: "preferred_model", validate: nullableString },
  autoSelectBest: { column: "auto_select_best", validate: bool },
  preferredAgentId: { column: "preferred_agent_id", validate: nullableString },
  selectedSuperPromptId: { column: "selected_super_prompt_id", validate: nullableString },
  thinkingMode: { column: "thinking_mode", validate: bool },
  webSearchMode: { column: "web_search_mode", validate: oneOf(["open", "closed"]) },
  contextMode: { column: "context_mode", validate: oneOf(["MAX", "LITE"]) },
  chatMode: { column: "chat_mode", validate: oneOf(["normal", "questioning", "dialogue"]) },
  useRag: { column: "use_rag", validate: bool },
  ragLimit: { column: "rag_limit", validate: intInRange(3, 20) },
  ragSimilarityThreshold: { column: "rag_similarity_threshold", validate: numInRange(0.2, 0.9) },
  ragHybridMode: { column: "rag_hybrid_mode", validate: bool },
  ragHybridSearch: { column: "rag_hybrid_search", validate: bool },
  ragUseRerank: { column: "rag_use_rerank", validate: bool },
  autoEmbedDocuments: { column: "auto_embed_documents", validate: bool },
  autoEmbedThreads: { column: "auto_embed_threads", validate: bool },
  streamTimeoutPreset: {
    column: "stream_timeout_preset",
    validate: oneOf(["5", "10", "15", "30", "custom", "none"]),
  },
  streamCustomTimeoutMinutes: { column: "stream_custom_timeout_minutes", validate: intInRange(1, 120) },
  voiceMode: { column: "voice_mode", validate: oneOf(["off", "record", "speech"]) },
  ttsVoiceId: { column: "tts_voice_id", validate: nullableString },
  ttsSpeed: { column: "tts_speed", validate: numInRange(0.5, 2.0) },
  uiPreferences: { column: "ui_preferences", validate: (v) => sanitizeUiPreferences(v) },
};

export const PREFERENCE_KEYS = Object.keys(FIELD_DEFS) as Array<keyof UserChatPreferences>;

/** DB row (snake_case) → client preferences, defaults applied for missing/null non-nullable fields. */
export function rowToPreferences(row: Record<string, unknown> | null | undefined): UserChatPreferences {
  const prefs = { ...DEFAULT_CHAT_PREFERENCES };
  if (!row) return prefs;
  for (const key of PREFERENCE_KEYS) {
    const { column, validate } = FIELD_DEFS[key];
    const raw = row[column];
    if (raw === undefined) continue;
    if (raw === null) {
      // Nullable columns (model/agent/prompt/voice ids) legitimately hold null.
      if (DEFAULT_CHAT_PREFERENCES[key] === null) (prefs as Record<string, unknown>)[key] = null;
      continue;
    }
    const valid = validate(raw);
    if (valid !== undefined) (prefs as Record<string, unknown>)[key] = valid;
  }
  return prefs;
}

/**
 * Partial client update (camelCase) → validated DB column map (snake_case).
 * Unknown keys and invalid values are dropped; returns the list of rejected keys.
 */
export function preferencesToRow(partial: Record<string, unknown>): {
  columns: Record<string, unknown>;
  rejected: string[];
} {
  const columns: Record<string, unknown> = {};
  const rejected: string[] = [];
  for (const [key, value] of Object.entries(partial)) {
    const def = FIELD_DEFS[key as keyof UserChatPreferences];
    if (!def) {
      rejected.push(key);
      continue;
    }
    if (value === null && DEFAULT_CHAT_PREFERENCES[key as keyof UserChatPreferences] === null) {
      columns[def.column] = null;
      continue;
    }
    const valid = def.validate(value);
    if (valid === undefined) {
      rejected.push(key);
      continue;
    }
    columns[def.column] = valid;
  }
  return { columns, rejected };
}
